import type { UseQueryOptions } from '@tanstack/react-query';
import { keepPreviousData, useQuery } from '@tanstack/react-query';

import { queryClient } from '@/api/query-client';
import type { ApiError } from '@/api/types';
import Api from '@/controllers/api';
import type { QueryHook } from '@/lib/react-query/react-query-types';
import type {
  IQueryMultiRequest,
  IQueryRequest,
} from '@/utils/build-query-params';
import buildFullQueryParams from '@/utils/build-query-params';

import type {
  WorkerQualificationRequest,
  WorkerQualificationResponse,
  WorkersListMultiselectRequest,
  WorkersListQueryRequest,
  WorkersListResponse,
} from './types';

export const invalidateWorkersList = () => {
  void queryClient?.invalidateQueries({ queryKey: ['workers', 'list'] });
};

export const useWorkersList = <Data = WorkersListResponse>({
  queryParams,
  multiSelects,
  options,
}: {
  queryParams?: WorkersListQueryRequest;
  multiSelects?: WorkersListMultiselectRequest;
  options?: Omit<
    UseQueryOptions<WorkersListResponse, ApiError, Data>,
    'queryKey' | 'queryFn'
  >;
}) => {
  return useQuery<WorkersListResponse, ApiError, Data>({
    queryKey: ['workers', 'list', queryParams, multiSelects],
    queryFn: ({ signal }) =>
      Api.get<WorkersListResponse>(
        buildFullQueryParams({
          path: '/workers',
          queryParams: queryParams as IQueryRequest,
          multiSelects: multiSelects as IQueryMultiRequest,
        }),
        {
          signal,
        },
      ).then(({ data }) => data),
    placeholderData: keepPreviousData,
    ...options,
  });
};

export const invalidateWorkerQualification = (workerId: string) => {
  void queryClient?.invalidateQueries({
    queryKey: ['workers', 'qualification', workerId],
  });
};

export const useWorkerQualification: QueryHook<
  WorkerQualificationRequest,
  WorkerQualificationResponse
> = ({ params, options }) => {
  return useQuery({
    queryKey: ['workers', 'qualification', params?.workerId],
    queryFn: async ({ signal }) => {
      return Api.get<WorkerQualificationResponse>(
        `/worker-qualification/${params?.workerId}`,
        { signal },
      ).then(({ data }) => data);
    },
    enabled: !!params?.workerId,
    ...options,
  });
};
